import { Client } from "@/Common/Api/Client";
import { IObstetricaLabResultOption, IObstetricaLabResultsGroup, LabObservationObstetricalFile, LabObservationResultType as LabObstetricalObservationResultType } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { BaseObstetricalLabResultHandler } from "./BaseObstetricalLabResultHandler";

class OntarioObstetricaLabResultHandler extends BaseObstetricalLabResultHandler {
    public get relevantStandardCodes(): { [key: string]: IObstetricaLabResultOption } {
        return this.getRelevantCodes();
    }

    public async getlabs(contactId: string): Promise<IObstetricaLabResultsGroup[]> {
        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_Patient_MedicalNote_GetLabResultsObstetricalFile,
            {
                PatientContactId: contactId,
                ObstetricalFileType: ObstetricalFileType.OntarioForm
            }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        return (response.data?.ObstetricalFileLabResult as LabObservationObstetricalFile[])
            ?.filter(r => this.relevantStandardCodes[r.ObservationStandardCode] != null)
            .sortByProperty(r => this.relevantStandardCodes[r.ObservationStandardCode].index)
            .groupBy(p => p.ObservationStandardCode)
            .map(item => ({
                key: item[0].ObservationStandardCode,
                values: item.sortByPropertyDesc(p => p.CollectionDate),
                options: this.relevantStandardCodes[item[0].ObservationStandardCode]
            }) as IObstetricaLabResultsGroup
            );
    }

    private getRelevantCodes(): { [key: string]: IObstetricaLabResultOption; } {
        return {
            // Hemoglobin
            "LOINC_718-7": {
                index: 0,
                displayTrimester: true,
                isValidRegex: /^\d{0,3}$/,
                labObservationResultType: LabObstetricalObservationResultType.Hemoglobin
            },

            // Hepatitis B virus surface Ag [Presence] in Serum
            "LOINC_5195-3": {
                index: 1000,
                displayTrimester: false,
                isValidRegex: /^(Non Reactive|Reactive)$/,
                labObservationResultType: LabObstetricalObservationResultType.HBVsurfAg
            },

            // ABO and Rh group [Type] in Blood
            "LOINC_882-1": {
                index: 2000,
                displayTrimester: true,
                isValidRegex: /^([ABO]|AB){1} RH (POSITIVE|NEGATIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.AboRhType,
                showResultTypeEvenIfThereIsNoRecentResult: true // Blood type dont change over type
            },

            // ABO group [Type] in Blood
            "LOINC_883-9": {
                index: 2001,
                displayTrimester: true,
                isValidRegex: /^[ABO]|AB$/,
                labObservationResultType: LabObstetricalObservationResultType.AboType,
                showResultTypeEvenIfThereIsNoRecentResult: true // Blood type dont change over type
            },

            // Rh [Type] in Blood
            "LOINC_10331-7": {
                index: 2002,
                displayTrimester: true,
                isValidRegex: /^(POSITIVE|NEGATIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.RhType,
                showResultTypeEvenIfThereIsNoRecentResult: true // Blood type dont change over type
            },

            // [Syphilis] Treponema pallidum IgG+IgM Ab [Presence] in Serum
            "LOINC_34147-9": {
                index: 3000,
                displayTrimester: false,
                isValidRegex: /^(Non Reactive|Reactive)$/,
                labObservationResultType: LabObstetricalObservationResultType.Syphilis
            },

            // MCV [Entitic volume] by Automated count
            "LOINC_787-2": {
                index: 4000,
                displayTrimester: false,
                isValidRegex: /^\d{0,3}$/,
                labObservationResultType: LabObstetricalObservationResultType.McvRbc
            },

            // HIV 1 and 2 Ab [Identifier] in Serum, Plasma or Blood by Rapid immunoassay
            "LOINC_80203-3": {
                index: 5000,
                displayTrimester: false,
                isValidRegex: /^(Non Reactive|Reactive)$/,
                labObservationResultType: LabObstetricalObservationResultType.HIV12Ab
            },

            // Blood group antibody screen [Presence] in Serum or Plasma
            "LOINC_890-4": {
                index: 6000,
                displayTrimester: true,
                isValidRegex: /^(NEGATIVE|POSITIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.BldGpAntibody
            },

            // Blood group antibody screen [Titer] in Serum or Plasma
            "LOINC_15343-7": {
                index: 6001,
                displayTrimester: true,
                isValidRegex: /Detected/gi,
                labObservationResultType: LabObstetricalObservationResultType.BldGpAntibody
            },

            // Platelets [#/volume] in Blood by Automated count
            "LOINC_777-3": {
                index: 7000,
                displayTrimester: true,
                isValidRegex: /^\d{0,4}$/,
                labObservationResultType: LabObstetricalObservationResultType.Platelet
            },

            // Chlamydia sp DNA [Presence] in Specimen by NAA with probe detection
            "LOINC_35729-3": {
                index: 8000,
                displayTrimester: false,
                isValidRegex: /^(NEGATIVE|POSITIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.Chlamydia
            },
            // Chlamydia trachomatis DNA [Presence] in Urine by NAA with probe detection
            "LOINC_6357-8": {
                index: 8001,
                displayTrimester: false,
                isValidRegex: /^(NEGATIVE|POSITIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.Chlamydia
            },
            // Chlamydia trachomatis DNA [Presence] in Specimen by NAA with probe detection
            "LOINC_21613-5": {
                index: 8002,
                displayTrimester: false,
                isValidRegex: /^(NEGATIVE|POSITIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.Chlamydia
            },
            // Chlamydia trachomatis+Neisseria gonorrhoeae DNA [Presence] in Urine by NAA with probe detection
            "LOINC_44806-8": {
                index: 8003,
                displayTrimester: false,
                isValidRegex: null,
                labObservationResultType: LabObstetricalObservationResultType.ChlamydiaGonorrhoeae
            },

            // Neisseria gonorrhoeae DNA [Presence] in Specimen by NAA with probe detection
            "LOINC_24111-7": {
                index: 9001,
                displayTrimester: false,
                isValidRegex: /^(NEGATIVE|POSITIVE)$/,
                labObservationResultType: LabObstetricalObservationResultType.Gonorrhoeae
            },

            // Rubella virus IgG Ab [Presence] in Serum or Plasma by Immunoassay
            "LOINC_40667-8": {
                index: 10000,
                displayTrimester: false,
                isValidRegex: /^(Non Reactive|Indeterminate|Reactive|(\d{1,2}(\.\d{0,2})?))$/,
                labObservationResultType: LabObstetricalObservationResultType.RubellaIgGAb,
                showResultTypeEvenIfThereIsNoRecentResult: true // immunisation last extremely long.
            },

            // Glucose [Moles/volume] in Serum or Plasma --1 hour post 50 g glucose PO
            "LOINC_14754-6": {
                index: 12000,
                displayTrimester: true,
                isValidRegex: /^\d{1,2}(\.\d{0,2})?$/,
                labObservationResultType: LabObstetricalObservationResultType.Glucose1h50g
            },

            // Glucose [Moles/volume] in Serum or Plasma --1 hour post 75 g glucose PO
            "LOINC_51597-3": {
                index: 13000,
                displayTrimester: true,
                isValidRegex: /^\d{1,2}(\.\d{0,2})?$/,
                labObservationResultType: LabObstetricalObservationResultType.Glucose1h75g
            },

            // Glucose [Moles/volume] in Serum or Plasma --2 hours post 75 g glucose PO
            "LOINC_14995-5": {
                index: 14000,
                displayTrimester: true,
                isValidRegex: /^\d{1,2}(\.\d{0,2})?$/,
                labObservationResultType: LabObstetricalObservationResultType.Glucose2h75g
            },

            // Urine Culture
            "UrineCS": {
                index: 16000,
                displayTrimester: false,
                isValidRegex: null,
                labObservationResultType: LabObstetricalObservationResultType.UrinceCS,
                showAsNote: true
            },

            // Aerobe Culture
            "LOINC_634-6": {
                index: 16001,
                displayTrimester: false,
                isValidRegex: null,
                labObservationResultType: LabObstetricalObservationResultType.UrinceCS,
                showAsNote: true
            }
        };
    }
}

export { OntarioObstetricaLabResultHandler };
